import React, { useState, useEffect } from 'react';
import { Operation, Team, AuditLog } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faClock, faExclamationTriangle, faCheckCircle, faTimesCircle, faCheckDouble, faMinus } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface OperationAnalysisProps {
  operation: Operation;
  teams: Team[];
  auditLogs: AuditLog[];
}

const OperationAnalysis: React.FC<OperationAnalysisProps> = ({
  operation,
  teams,
  auditLogs
}) => {
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  useEffect(() => {
    // Select all teams by default
    setSelectedTeams(teams.map(team => team.name));
  }, [teams]);

  // Helper function to get display name for a team
  const getTeamDisplayName = (team: Team) => {
    const isEmergency = team.emergencyTrupp === 1 || team.emergencyTrupp === '1' || team.emergencyTrupp === true;
    return isEmergency ? 'Notfalltrupp' : team.name;
  };

  // Helper function to check if team is emergency team
  const isEmergencyTeam = (team: Team) => {
    return team.emergencyTrupp === 1 || team.emergencyTrupp === '1' || team.emergencyTrupp === true;
  };

  const getEventIcon = (logType: string, teamName?: string, hasTeamID?: boolean) => {
    // Get team color based on team name
    const getTeamColor = (teamName: string) => {
      const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336'];
      const teamIndex = teams.findIndex(team => team.name === teamName);
      return teamIndex >= 0 ? colors[teamIndex % colors.length] : '#2196f3';
    };

    // Use gray color for logs without teamID, otherwise use team color
    const iconColor = hasTeamID && teamName ? getTeamColor(teamName) : '#999999';

    switch (logType.toLowerCase()) {
      case 'mayday':
        return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#d32f2f' }} />;
      case 'status_update':
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: iconColor }} />;
      case 'team_removed':
        return <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#ff9800' }} />;
      default:
        return <FontAwesomeIcon icon={faClock} style={{ color: iconColor }} />;
    }
  };

  const getEventColor = (shortDescription: string, teamColor?: string) => {
    const desc = shortDescription.toLowerCase();
    
    if (desc.includes('mayday')) {
      return '#d32f2f'; // Rot für Mayday
    } else {
      return teamColor || '#2196f3'; // Trupp-Farbe für alle anderen Events
    }
  };

  const handleTeamToggle = (teamName: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamName) 
        ? prev.filter(name => name !== teamName)
        : [...prev, teamName]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map(team => team.name));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const prepareTimelineData = () => {
    if (!operation || !teams.length) return null;

    const events: any[] = [];
    const operationStart = new Date(operation.created);

    // Add audit log events - only show logs that have a valid teamID
    auditLogs.forEach(log => {
      if (log.created_at && log.teamID) {
        // Find the team this log belongs to by teamID
        const assignedTeam = teams.find(team => team.id === log.teamID);
        
        if (assignedTeam) {
          // Find team color for this event
          const teamIndex = teams.findIndex(team => team.name === assignedTeam.name);
          const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336'];
          const teamColor = teamIndex >= 0 ? colors[teamIndex % colors.length] : '#2196f3';
          
          events.push({
            time: new Date(log.created_at),
            type: 'audit_log',
            label: log.shortDescription || log.logType,
            team: assignedTeam.name,
            logType: log.logType,
            color: getEventColor(log.shortDescription || log.logType, teamColor),
            description: log.shortDescription || log.message || log.logType,
            logData: log.logData
          });
        }
      }
    });

    // Sort events by time
    events.sort((a, b) => a.time.getTime() - b.time.getTime());

    return events;
  };

  const prepareBarStatusData = () => {
    if (!teams.length || !operation || !auditLogs.length) return null;

    const operationStart = new Date(operation.created);
    const datasets = teams
      .filter(team => selectedTeams.includes(team.name))
      .map((team, index) => {
        const isEmergency = isEmergencyTeam(team);
        const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336'];
        const color = isEmergency ? '#d32f2f' : colors[index % colors.length];
        const displayName = getTeamDisplayName(team);

        // Find the first log entry for this team (trupp erstellt)
        const teamCreationLog = auditLogs.find(log => {
          const isTeamLog = log.message && log.message.includes(team.name);
          const isCreationLog = log.shortDescription && log.shortDescription.toLowerCase().includes('trupp erstellt');
          return isTeamLog && isCreationLog && log.created_at;
        });

        // Filter audit logs for this team's status updates and pressure adjustments
        const teamPressureLogs = auditLogs.filter(log => {
          // Check if log belongs to this team by teamID
          const isTeamLog = log.teamID && log.teamID === team.id;
          // Check if it's a status update or pressure adjustment
          const isStatusUpdate = log.shortDescription && log.shortDescription.toLowerCase().includes('statusupdate');
          const isPressureAdjustment = (log.logType && log.logType.toLowerCase().includes('druckanpassung')) ||
                                      (log.shortDescription && log.shortDescription.toLowerCase().includes('druckanpassung'));
          return isTeamLog && (isStatusUpdate || isPressureAdjustment) && log.created_at;
        });

        // Extract bar values from logData
        const dataPoints = teamPressureLogs.map(log => {
          let barValue = null;
          
          if (log.logData) {
            try {
              const parsedLogData = typeof log.logData === 'string' ? JSON.parse(log.logData) : log.logData;
              // Check for bar value in status updates
              if (parsedLogData && parsedLogData.bar !== undefined && parsedLogData.bar !== null) {
                barValue = Number(parsedLogData.bar);
              }
              // Check for finalPressure in pressure adjustments
              else if (parsedLogData && parsedLogData.finalPressure !== undefined && parsedLogData.finalPressure !== null) {
                barValue = Number(parsedLogData.finalPressure);
              }
            } catch (e) {
              console.warn('Could not parse logData for bar value:', log.logData);
            }
          }
          
          return {
            x: new Date(log.created_at!),
            y: barValue
          };
        }).filter(point => point.y !== null); // Remove points without bar data

        // Add initial pressure from team creation log if available
        if (teamCreationLog && teamCreationLog.logData) {
          try {
            const parsedLogData = typeof teamCreationLog.logData === 'string' ? JSON.parse(teamCreationLog.logData) : teamCreationLog.logData;
            if (parsedLogData && parsedLogData.bar !== undefined && parsedLogData.bar !== null) {
              const initialBarValue = Number(parsedLogData.bar);
              dataPoints.unshift({
                x: new Date(teamCreationLog.created_at!),
                y: initialBarValue
              });
            }
          } catch (e) {
            console.warn('Could not parse logData for initial bar value:', teamCreationLog.logData);
          }
        }

        // Sort by time
        dataPoints.sort((a, b) => a.x.getTime() - b.x.getTime());

        return {
          label: displayName,
          data: dataPoints,
          borderColor: color,
          backgroundColor: color + '20',
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          pointRadius: 4,
          pointHoverRadius: 6
        };
      });

    return {
      datasets
    };
  };

  const timelineData = prepareTimelineData();
  const barStatusData = prepareBarStatusData();

  return (
    <div>
      {/* Team Filter */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>
          <FontAwesomeIcon icon={faUsers} style={{ marginRight: '10px' }} />
          Trupp-Filter
        </h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={handleSelectAllTeams}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
          >
            <FontAwesomeIcon icon={faCheckDouble} />
            Alle auswählen
          </button>
          <button
            onClick={handleDeselectAllTeams}
            style={{
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
          >
            <FontAwesomeIcon icon={faMinus} />
            Alle abwählen
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {teams.map((team, index) => {
            const isEmergency = isEmergencyTeam(team);
            const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336'];
            const color = isEmergency ? '#d32f2f' : colors[index % colors.length];
            const isSelected = selectedTeams.includes(team.name);
            const displayName = getTeamDisplayName(team);
            
            return (
              <button
                key={team.id}
                onClick={() => handleTeamToggle(team.name)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: isSelected ? color : '#f5f5f5',
                  color: isSelected ? 'white' : '#333',
                  border: `2px solid ${color}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  transition: 'all 0.2s ease'
                }}
              >
                {displayName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline Chart */}
      {timelineData && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
          marginBottom: '20px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
            <FontAwesomeIcon icon={faClock} style={{ marginRight: '10px' }} />
            Einsatz-Zeitachse
          </h2>
          
          <div style={{ height: '400px' }}>
            <Line
              data={{
                datasets: teams
                  .filter(team => selectedTeams.includes(team.name))
                  .map((team, teamIndex) => {
                    const teamEvents = timelineData.filter(event => event.team === team.name);
                    const isEmergency = isEmergencyTeam(team);
                    const colors = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336'];
                    const teamColor = isEmergency ? '#d32f2f' : colors[teamIndex % colors.length];
                    const displayName = getTeamDisplayName(team);
                  
                  return {
                    label: displayName,
                    data: teamEvents.map(event => ({
                      x: event.time,
                      y: teamIndex + 1,
                      event: event
                    })),
                    borderColor: teamColor,
                    backgroundColor: teamColor + '20',
                    borderWidth: 2,
                    pointBackgroundColor: teamEvents.map(event => event.color),
                    pointBorderColor: teamEvents.map(event => event.color),
                    pointRadius: 8,
                    pointHoverRadius: 12,
                    fill: false,
                    tension: 0,
                    pointStyle: 'circle'
                  };
                })
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  intersect: false,
                  mode: 'nearest'
                },
                scales: {
                  x: {
                    type: 'time',
                    time: {
                      unit: 'minute',
                      displayFormats: {
                        minute: 'HH:mm',
                        hour: 'HH:mm'
                      },
                      tooltipFormat: 'dd.MM.yyyy HH:mm:ss'
                    },
                    title: {
                      display: true,
                      text: 'Zeit'
                    },
                    min: timelineData[0]?.time.getTime(),
                    max: timelineData[timelineData.length - 1]?.time.getTime()
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Trupps'
                    },
                    min: 0,
                    max: teams.length + 1,
                    ticks: {
                      stepSize: 1,
                      callback: function(value) {
                        if (value === 0) return '';
                        if ((value as number) <= teams.length) {
                          const team = teams[(value as number) - 1];
                          return team ? getTeamDisplayName(team) : '';
                        }
                        return '';
                      }
                    }
                  }
                },
                plugins: {
                  tooltip: {
                    enabled: true,
                    callbacks: {
                      title: function(context: any) {
                        const event = context[0].raw?.event;
                        if (event) {
                          return format(event.time, 'dd.MM.yyyy HH:mm:ss', { locale: de });
                        }
                        return '';
                      },
                      label: function(context: any) {
                        const event = context.raw?.event;
                        if (event) {
                          const labels = [`Ereignis: ${event.label}`];
                          
                          // Add logData if available and not empty
                          if (event.logData) {
                            try {
                              const parsedLogData = typeof event.logData === 'string' ? JSON.parse(event.logData) : event.logData;
                              if (parsedLogData && Object.keys(parsedLogData).length > 0) {
                                Object.entries(parsedLogData).forEach(([key, value]) => {
                                  // Skip redundant fields
                                  const skipFields = ['name', 'mitglieder', 'truppId', 'einsatzId', 'id', 'einsatzId', 'alt', 'neu', 'abfrageCountdown', 'abfragecountdown', 'abfrage_countdown', 'countdown', 'truppid', 'einsatzid', 'trupp_id', 'einsatz_id', 'restzeit', 'restzeitcountdown', 'restzeit_countdown', 'restzeit countdown', 'performedbyid', 'performed_by_id'];
                                  if (skipFields.includes(key.toLowerCase())) {
                                    return;
                                  }
                                  
                                  if (value !== null && value !== undefined && value !== '') {
                                    let formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
                                    
                                    // Rename specific fields
                                    if (key.toLowerCase() === 'finalpressure' || key.toLowerCase() === 'final_pressure') {
                                      formattedKey = 'Bar';
                                    }
                                    if (key.toLowerCase() === 'performedby' || key.toLowerCase() === 'performed_by') {
                                      formattedKey = 'Truppüberwacher';
                                    }
                                    
                                    const formattedValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
                                    labels.push(`${formattedKey}: ${formattedValue}`);
                                  }
                                });
                              }
                            } catch (e) {
                              // If logData is not valid JSON, show it as is
                              if (event.logData && event.logData !== '{}' && event.logData !== 'null') {
                                labels.push(`Daten: ${event.logData}`);
                              }
                            }
                          }
                          
                          return labels;
                        }
                        return '';
                      }
                    }
                  },
                  legend: {
                    display: true,
                    position: 'top'
                  }
                }
              }}
            />
          </div>

          {/* Event Legend */}
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>Ereignis-Typen:</h3>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#d32f2f'
                }} />
                <span>Mayday (rot)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%',
                  backgroundColor: '#666'
                }} />
                <span>Andere Events (Trupp-Farbe)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bar Status Chart */}
      {barStatusData && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
          marginBottom: '20px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
            <FontAwesomeIcon icon={faUsers} style={{ marginRight: '10px' }} />
            Bar-Status pro Trupp
          </h2>
          
          <div style={{ height: '400px' }}>
            <Line
              data={barStatusData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  x: {
                    type: 'time',
                    time: {
                      unit: 'minute',
                      displayFormats: {
                        minute: 'HH:mm',
                        hour: 'HH:mm'
                      },
                      tooltipFormat: 'dd.MM.yyyy HH:mm:ss'
                    },
                    title: {
                      display: true,
                      text: 'Zeit'
                    },
                    min: undefined
                  },
                  y: {
                    title: {
                      display: true,
                      text: 'Bar (bar)'
                    },
                    min: 0,
                    max: 300
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      title: function(context) {
                        return format(new Date(context[0].parsed.x), 'dd.MM.yyyy HH:mm:ss', { locale: de });
                      },
                      label: function(context) {
                        return `${context.parsed.y} bar`;
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Audit Logs */}
      {auditLogs.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #ececec',
          marginBottom: '20px'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
            <FontAwesomeIcon icon={faClock} style={{ marginRight: '10px' }} />
            Audit-Log ({auditLogs
              .filter(log => {
                // Find team by teamID or extract from message
                let teamName: string | undefined;
                
                if (log.teamID) {
                  const team = teams.find(team => team.id === log.teamID);
                  teamName = team?.name;
                } else {
                  // Fallback: extract team name from log message
                  const teamMatch = teams.find(team => log.message && log.message.includes(team.name));
                  teamName = teamMatch?.name;
                }
                
                // Show log if no team is assigned or if team is selected
                return !teamName || selectedTeams.includes(teamName);
              }).length} Einträge)
          </h2>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {auditLogs
              .filter(log => {
                // Find team by teamID or extract from message
                let teamName: string | undefined;
                
                if (log.teamID) {
                  const team = teams.find(team => team.id === log.teamID);
                  teamName = team?.name;
                } else {
                  // Fallback: extract team name from log message
                  const teamMatch = teams.find(team => log.message && log.message.includes(team.name));
                  teamName = teamMatch?.name;
                }
                
                // Show log if no team is assigned or if team is selected
                return !teamName || selectedTeams.includes(teamName);
              })
              .map((log, index) => {
                // Find team by teamID or extract from message
                let teamName: string | undefined;
                const hasTeamID = !!log.teamID;
                
                if (log.teamID) {
                  const team = teams.find(team => team.id === log.teamID);
                  teamName = team?.name;
                } else {
                  // Fallback: extract team name from log message
                  const teamMatch = teams.find(team => log.message && log.message.includes(team.name));
                  teamName = teamMatch?.name;
                }

              return (
                <div key={log.id || index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '12px',
                  borderBottom: index < auditLogs.length - 1 ? '1px solid #eee' : 'none',
                  backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
                }}>
                  <div style={{ fontSize: '16px' }}>
                    {getEventIcon(log.logType, teamName, hasTeamID)}
                  </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#333' }}>
                    {log.shortDescription || log.logType}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {log.message}
                  </div>
                </div>
                                 <div style={{ fontSize: '12px', color: '#999', minWidth: '120px' }}>
                   {log.created_at ? format(new Date(log.created_at), 'dd.MM.yyyy HH:mm:ss', { locale: de }) : 'Keine Zeit'}
                 </div>
               </div>
             );
           })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationAnalysis;
